import axios, { AxiosPromise } from "axios";
import qs from "qs";
import {
  VideoDetails,
  RailsFormErrors,
  FetchParams,
  VideoFormData,
  OnUploadProgress,
} from "../types";
import { FIELD } from "../constants";
import { ChunkService } from "./chunk.service";

type VideoService = {
  urlBase: string;
  url: (id?: number) => string;
  fetchOne: (id: number, increaseView?: boolean) => AxiosPromise<VideoDetails>;
  fetchOneByShareId: (id: string) => AxiosPromise<VideoDetails>;
  fetchAll: (params: FetchParams) => AxiosPromise<VideoDetails[]>;
  create: (
    video: VideoFormData,
    onUploadProgress: OnUploadProgress
  ) => AxiosPromise<{
    success: boolean;
    instance: VideoDetails;
    errors: RailsFormErrors;
  }>;
  update: (
    video: VideoFormData,
    id: number
  ) => AxiosPromise<{
    success: boolean;
    instance: VideoDetails;
    errors: RailsFormErrors;
  }>;
  delete: (id: number) => AxiosPromise;
  getTemporaryLink: (id: number, duration?: number) => AxiosPromise;
};

export const VideoService: VideoService = {
  urlBase: "/api/ikm/v1/videos",
  url: (id?: number): string => VideoService.urlBase + ((id && "/" + id) || ""),
  fetchOne: (id: number, increaseView?: boolean) =>
    axios.get(VideoService.url(id) + (increaseView ? "?view=true" : "")),
  fetchOneByShareId: (id: string) => axios.get(`/shares/${id}/data`),
  fetchAll: (params) => {
    const transformedParams: any = {
      params: {
        [FIELD.PAGE]: params.pageNumber,
        [FIELD.PER_PAGE]: params.pageSize,
        [FIELD.ORDER]: {
          [FIELD.FIELD]: ["created_at"],
          [FIELD.DIRECTION]: ["desc"],
        },
        data: {
          logicOperator: "and",
          quickFilterLogicOperator: "and",
        },
      },
    };
    return axios.get(VideoService.url(), transformedParams);
  },
  create: async (video: VideoFormData, onUploadProgress: OnUploadProgress) => {
    const { body, headers } = await prepareData(video, onUploadProgress, true);
    return axios.post(VideoService.url(), body, { headers, onUploadProgress });
  },
  update: async (video: VideoFormData, id: number) => {
    const { body, headers } = await prepareData(video, undefined, false);
    return axios.put(VideoService.url(id), body, { headers });
  },
  delete: (id: number): AxiosPromise =>
    axios({
      method: "delete",
      url: VideoService.url(id),
    }),
  getTemporaryLink: (id: number, duration?: number): AxiosPromise =>
    axios({
      method: "post",
      url: VideoService.url(id) + "/shares",
      data: qs.stringify({ ...(duration && { validity_time: duration }) }),
    }),
};

export const transformData = (
  video: VideoFormData,
  onUploadProgress?: OnUploadProgress,
  isCreate = false
) => {
  const baseData: any = {
    "video[title]": video.title,
    "video[category_ids]": video.category_ids,
    "video[enabled]": video.enabled,
    "video[is_ai]": video.is_ai,
    "video[downloadable]": video.downloadable,
    "video[for_internal_use]": video.for_internal_use,
    ...(video.description.length > 0 && {
      "video[description]": video.description,
    }),
    ...(video.speakers.length > 0 && { "video[speakers]": video.speakers }),
    ...(video.tags &&
      video.tags.length && { "video[tags]": video.tags.map((tag) => tag.id) }),
    ...(video.timestamps &&
      video.timestamps.length && {
        "video[time_stamps]": video.timestamps.map((item) => ({
          starts_at: item.starts_at,
          ...(item.text !== "" && { text: item.text }),
        })),
      }),
  };

  if (isCreate && video.attachment && video.attachment[0] && onUploadProgress) {
    return ChunkService.upload(video.attachment[0], onUploadProgress).then(
      (batchId) =>
        qs.stringify(
          { ...baseData, chunks_batch_id: batchId },
          { arrayFormat: "brackets" }
        )
    );
  }

  return qs.stringify(baseData, { arrayFormat: "brackets" });
};

const prepareData = async (
  video: VideoFormData,
  onUploadProgress?: OnUploadProgress,
  isCreate = false
): Promise<{ body: any; headers: any }> => {
  if (
    (video.preview && video.preview[0]) ||
    (video.subtitle && video.subtitle[0])
  ) {
    const baseData = await transformData(video, onUploadProgress, isCreate);
    const formData = new FormData();

    new URLSearchParams(baseData).forEach((value, key) => {
      formData.append(key, value);
    });

    if (video.preview && video.preview[0]) {
      formData.append("video[preview]", video.preview[0]);
    }

    if (video.subtitle && video.subtitle[0]) {
      formData.append("video[subtitle]", video.subtitle[0]);
    }

    return {
      body: formData,
      headers: {}, // FromData and the Browser know wich headers needed.
    };
  }

  // Return a default value if the condition is not met
  return {
    body: await transformData(video, onUploadProgress, isCreate),
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  };
};

export default VideoService;
